/**
 * Curated Altema tag id → English/JP labels.
 * Existing entries are frozen: refresh only *adds* missing ids.
 *
 *   npm run altema:refresh-tags
 *
 * Orphaned ids stay omitted — see ORPHANED_TAG_IDS.
 *
 * @typedef {{ en: string, jp: string, group: string, appliesTo: Array<"unit"|"equip"> }} TagDef
 */

/** @type {Record<number, TagDef>} */
export const TAG_DICTIONARY = {
  0: { en: "Raid / Side Story", jp: "襲来・外伝", group: "Source", appliesTo: ["unit"] },
  1: { en: "DMG Inflicting UP", jp: "与ダメージUP", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  2: { en: "DMG Taken UP", jp: "被ダメージUP", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  3: { en: "Physical DMG UP", jp: "物理ダメージUP", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  4: { en: "Physical RES DOWN", jp: "物理耐性DOWN", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  5: { en: "Magic DMG UP", jp: "魔法ダメージUP", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  6: { en: "Magic RES DOWN", jp: "魔法耐性DOWN", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  7: { en: "Giant Boss Killer", jp: "大型キラー", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  8: { en: "DMG UP during Break", jp: "ブレイク中ダメージUP", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  9: { en: "CRI DMG UP", jp: "CRIダメージUP", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  10: { en: "CRI Rate UP", jp: "クリティカル率UP", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  11: { en: "Ignore DEF", jp: "防御無視", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  12: { en: "CRI RES DOWN", jp: "CRI耐性DOWN", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  13: { en: "Super CRI Rate UP", jp: "スーパーCRI率UP", group: "Increasing DMG", appliesTo: ["unit"] },
  14: { en: "Bleed", jp: "出血", group: "Increasing DMG", appliesTo: ["unit","equip"] },
  15: { en: "DMG Taken DOWN", jp: "被ダメージ軽減", group: "Reducing DMG", appliesTo: ["unit","equip"] },
  16: { en: "Physical DMG DOWN", jp: "物理ダメージ軽減", group: "Reducing DMG", appliesTo: ["unit","equip"] },
  17: { en: "Magic DMG DOWN", jp: "魔法ダメージ軽減", group: "Reducing DMG", appliesTo: ["unit","equip"] },
  18: { en: "Negates DMG", jp: "ダメージ無効化", group: "Reducing DMG", appliesTo: ["unit","equip"] },
  19: { en: "Heal on DMG Taken", jp: "被ダメージ回復", group: "Reducing DMG", appliesTo: ["unit","equip"] },
  20: { en: "DMG Inflicting DOWN", jp: "与ダメージDOWN", group: "Reducing DMG", appliesTo: ["unit","equip"] },
  21: { en: "Reflecting DMG", jp: "ダメージ反射", group: "Reducing DMG", appliesTo: ["unit"] },
  22: { en: "Taunting", jp: "庇う", group: "Reducing DMG", appliesTo: ["unit","equip"] },
  23: { en: "CRI RES UP", jp: "クリティカル耐性UP", group: "Reducing DMG", appliesTo: ["unit","equip"] },
  24: { en: "Restore \"DMG Taken UP\"", jp: "被ダメージアップ回復", group: "Reducing DMG", appliesTo: ["unit"] },
  25: { en: "Human Killer", jp: "人族キラー", group: "Species Killer", appliesTo: ["unit","equip"] },
  26: { en: "Demon Killer", jp: "魔族キラー", group: "Species Killer", appliesTo: ["unit","equip"] },
  27: { en: "God Killer", jp: "神族キラー", group: "Species Killer", appliesTo: ["unit","equip"] },
  28: { en: "Dragon Killer", jp: "竜族キラー", group: "Species Killer", appliesTo: ["unit","equip"] },
  29: { en: "Beast Killer", jp: "獣族キラー", group: "Species Killer", appliesTo: ["unit","equip"] },
  30: { en: "Machine Killer", jp: "機族キラー", group: "Species Killer", appliesTo: ["unit","equip"] },
  31: { en: "Spirit Killer", jp: "精霊族キラー", group: "Species Killer", appliesTo: ["unit","equip"] },
  32: { en: "Giant Killer", jp: "巨人族キラー", group: "Species Killer", appliesTo: ["unit","equip"] },
  33: { en: "Fire Killer", jp: "炎属性キラー", group: "Elements Killer", appliesTo: ["unit","equip"] },
  34: { en: "Water Killer", jp: "水属性キラー", group: "Elements Killer", appliesTo: ["unit","equip"] },
  35: { en: "Earth Killer", jp: "樹属性キラー", group: "Elements Killer", appliesTo: ["unit","equip"] },
  36: { en: "Light Killer", jp: "光属性キラー", group: "Elements Killer", appliesTo: ["unit","equip"] },
  37: { en: "Dark Killer", jp: "闇属性キラー", group: "Elements Killer", appliesTo: ["unit","equip"] },
  38: { en: "Fire RES", jp: "炎属性耐性", group: "Elemental RES", appliesTo: ["unit","equip"] },
  39: { en: "Water RES", jp: "水属性耐性", group: "Elemental RES", appliesTo: ["unit","equip"] },
  40: { en: "Earth RES", jp: "樹属性耐性", group: "Elemental RES", appliesTo: ["unit","equip"] },
  41: { en: "Light RES", jp: "光属性耐性", group: "Elemental RES", appliesTo: ["unit","equip"] },
  42: { en: "Dark RES", jp: "闇属性耐性", group: "Elemental RES", appliesTo: ["unit","equip"] },
  43: { en: "Fire RES DOWN", jp: "炎属性耐性DOWN", group: "Reducing Elemental RES", appliesTo: ["unit","equip"] },
  44: { en: "Water RES DOWN", jp: "水属性耐性DOWN", group: "Reducing Elemental RES", appliesTo: ["unit","equip"] },
  45: { en: "Earth RES DOWN", jp: "樹属性耐性DOWN", group: "Reducing Elemental RES", appliesTo: ["unit","equip"] },
  46: { en: "Light RES DOWN", jp: "光属性耐性DOWN", group: "Reducing Elemental RES", appliesTo: ["unit","equip"] },
  47: { en: "Dark RES DOWN", jp: "闇属性耐性DOWN", group: "Reducing Elemental RES", appliesTo: ["unit","equip"] },
  48: { en: "Burn", jp: "燃焼付与", group: "Inflicting Status Ailment", appliesTo: ["unit","equip"] },
  49: { en: "Poison", jp: "毒付与", group: "Inflicting Status Ailment", appliesTo: ["unit","equip"] },
  50: { en: "Freeze", jp: "氷結付与", group: "Inflicting Status Ailment", appliesTo: ["unit","equip"] },
  51: { en: "Paralysis", jp: "麻痺付与", group: "Inflicting Status Ailment", appliesTo: ["unit","equip"] },
  52: { en: "Faint", jp: "気絶付与", group: "Inflicting Status Ailment", appliesTo: ["unit","equip"] },
  53: { en: "Blind", jp: "暗闇付与", group: "Inflicting Status Ailment", appliesTo: ["unit","equip"] },
  54: { en: "Disease", jp: "病気付与", group: "Inflicting Status Ailment", appliesTo: ["unit","equip"] },
  56: { en: "Seal", jp: "封印付与", group: "Inflicting Status Ailment", appliesTo: ["unit"] },
  57: { en: "Burn RES", jp: "燃焼耐性", group: "Resisting Status Ailment", appliesTo: ["unit","equip"] },
  58: { en: "Poison RES", jp: "毒耐性", group: "Resisting Status Ailment", appliesTo: ["unit","equip"] },
  59: { en: "Freeze RES", jp: "氷結耐性", group: "Resisting Status Ailment", appliesTo: ["unit","equip"] },
  60: { en: "Paralysis RES", jp: "麻痺耐性", group: "Resisting Status Ailment", appliesTo: ["unit","equip"] },
  61: { en: "Faint RES", jp: "気絶耐性", group: "Resisting Status Ailment", appliesTo: ["equip"] },
  62: { en: "Blind RES", jp: "暗闇耐性", group: "Resisting Status Ailment", appliesTo: ["unit","equip"] },
  63: { en: "Disease RES", jp: "病気耐性", group: "Resisting Status Ailment", appliesTo: ["unit","equip"] },
  64: { en: "Curse RES", jp: "呪い耐性", group: "Resisting Status Ailment", appliesTo: ["unit","equip"] },
  65: { en: "Seal RES", jp: "封印耐性", group: "Resisting Status Ailment", appliesTo: ["unit","equip"] },
  66: { en: "Cleanse Burn", jp: "燃焼回復", group: "Cleansing Status Ailment", appliesTo: ["unit","equip"] },
  67: { en: "Cleanse Poison", jp: "毒回復", group: "Cleansing Status Ailment", appliesTo: ["unit","equip"] },
  68: { en: "Cleanse Freeze", jp: "氷結回復", group: "Cleansing Status Ailment", appliesTo: ["unit","equip"] },
  69: { en: "Cleanse Paralysis", jp: "麻痺回復", group: "Cleansing Status Ailment", appliesTo: ["unit","equip"] },
  71: { en: "Cleanse Blind", jp: "暗闇回復", group: "Cleansing Status Ailment", appliesTo: ["unit","equip"] },
  72: { en: "Cleanse Disease", jp: "病気回復", group: "Cleansing Status Ailment", appliesTo: ["unit","equip"] },
  73: { en: "Cleanse Curse", jp: "呪い回復", group: "Cleansing Status Ailment", appliesTo: ["unit","equip"] },
  74: { en: "Cleanse Seal", jp: "封印回復", group: "Cleansing Status Ailment", appliesTo: ["unit","equip"] },
  75: { en: "Burn RES DOWN", jp: "燃焼耐性DOWN", group: "Reducing Status Ailment RES", appliesTo: ["unit","equip"] },
  76: { en: "Poison RES DOWN", jp: "毒耐性DOWN", group: "Reducing Status Ailment RES", appliesTo: ["unit","equip"] },
  77: { en: "Freeze RES DOWN", jp: "氷結耐性DOWN", group: "Reducing Status Ailment RES", appliesTo: ["unit","equip"] },
  78: { en: "Paralysis RES DOWN", jp: "麻痺耐性DOWN", group: "Reducing Status Ailment RES", appliesTo: ["unit","equip"] },
  79: { en: "Faint RES DOWN", jp: "気絶耐性DOWN", group: "Reducing Status Ailment RES", appliesTo: ["unit","equip"] },
  80: { en: "Blind RES DOWN", jp: "暗闇耐性DOWN", group: "Reducing Status Ailment RES", appliesTo: ["unit","equip"] },
  81: { en: "Disease RES DOWN", jp: "病気耐性DOWN", group: "Reducing Status Ailment RES", appliesTo: ["unit","equip"] },
  82: { en: "Curse RES DOWN", jp: "呪い耐性DOWN", group: "Reducing Status Ailment RES", appliesTo: ["unit","equip"] },
  83: { en: "Seal RES DOWN", jp: "封印耐性DOWN", group: "Reducing Status Ailment RES", appliesTo: ["unit","equip"] },
  84: { en: "HP Recovery", jp: "HP回復", group: "HP Recovery / Reviving", appliesTo: ["unit","equip"] },
  85: { en: "HP Auto-Recovery", jp: "HP自然回復", group: "HP Recovery / Reviving", appliesTo: ["unit","equip"] },
  86: { en: "Lifesteal", jp: "HP吸収", group: "HP Recovery / Reviving", appliesTo: ["unit","equip"] },
  87: { en: "Healing Rate UP", jp: "与回復量UP", group: "HP Recovery / Reviving", appliesTo: ["unit","equip"] },
  88: { en: "Recovery Rate UP", jp: "被回復量UP", group: "HP Recovery / Reviving", appliesTo: ["unit","equip"] },
  89: { en: "Self-Revive", jp: "自身を蘇生", group: "HP Recovery / Reviving", appliesTo: ["unit"] },
  90: { en: "Revive Allies", jp: "味方を蘇生", group: "HP Recovery / Reviving", appliesTo: ["unit","equip"] },
  91: { en: "Arts Gauge UP", jp: "奥義ゲージ上昇", group: "Arts Gauge", appliesTo: ["unit","equip"] },
  92: { en: "Auto-filling Arts Gauge", jp: "奥義ゲージ自然上昇", group: "Arts Gauge", appliesTo: ["unit","equip"] },
  93: { en: "Arts Gauge DOWN", jp: "奥義ゲージ減少", group: "Arts Gauge", appliesTo: ["unit"] },
  94: { en: "Stealing Arts Gauge", jp: "奥義ゲージ吸収", group: "Arts Gauge", appliesTo: ["unit","equip"] },
  95: { en: "BE Output UP", jp: "BE出現量UP", group: "Arts Gauge", appliesTo: ["unit","equip"] },
  96: { en: "Equip CT Speed UP", jp: "装備CT速度UP", group: "Speed / CT", appliesTo: ["unit","equip"] },
  97: { en: "Skill CT Speed UP", jp: "スキルCT速度UP", group: "Speed / CT", appliesTo: ["unit","equip"] },
  98: { en: "Action Speed DOWN", jp: "行動速度DOWN", group: "Speed / CT", appliesTo: ["unit"] },
  99: { en: "Accuracy UP", jp: "命中率UP", group: "Accuracy / Evasion", appliesTo: ["unit","equip"] },
  100: { en: "Accuracy DOWN", jp: "命中率DOWN", group: "Accuracy / Evasion", appliesTo: ["unit","equip"] },
  101: { en: "Evasion UP", jp: "回避率UP", group: "Accuracy / Evasion", appliesTo: ["unit","equip"] },
  102: { en: "Evasion DOWN", jp: "回避率DOWN", group: "Accuracy / Evasion", appliesTo: ["unit","equip"] },
  103: { en: "HP UP", jp: "HP UP", group: "Status", appliesTo: ["unit","equip"] },
  104: { en: "HP DOWN", jp: "HP DOWN", group: "Status", appliesTo: ["equip"] },
  105: { en: "ATK UP", jp: "攻撃力UP", group: "Status", appliesTo: ["unit","equip"] },
  106: { en: "ATK DOWN", jp: "攻撃力DOWN", group: "Status", appliesTo: ["unit","equip"] },
  107: { en: "DEF UP", jp: "防御力UP", group: "Status", appliesTo: ["unit","equip"] },
  108: { en: "DEF DOWN", jp: "防御力DOWN", group: "Status", appliesTo: ["unit","equip"] },
  109: { en: "Break Power UP", jp: "ブレイク力UP", group: "Break", appliesTo: ["unit","equip"] },
  110: { en: "Break RES DOWN", jp: "ブレイク耐性DOWN", group: "Break", appliesTo: ["unit"] },
  111: { en: "Break Gauge % Reduction", jp: "ブレイクゲージ割合減", group: "Break", appliesTo: ["unit"] },
  112: { en: "Hellfire RES", jp: "炎獄耐性", group: "Field Effect", appliesTo: ["unit","equip"] },
  113: { en: "Miasma Adaption", jp: "瘴気適正", group: "Field Effect", appliesTo: ["unit"] },
  114: { en: "Player EXP UP", jp: "プレイヤー経験値UP", group: "Increasing Acquisition", appliesTo: ["unit"] },
  115: { en: "Unit EXP UP", jp: "ユニット経験値UP", group: "Increasing Acquisition", appliesTo: ["equip"] },
  116: { en: "Gren UP", jp: "グランUP", group: "Increasing Acquisition", appliesTo: ["unit"] },
  117: { en: "Fire Relic", jp: "緋器", group: "Combination Effect", appliesTo: ["equip"] },
  118: { en: "Water Relic", jp: "蒼器", group: "Combination Effect", appliesTo: ["equip"] },
  119: { en: "Earth Relic", jp: "翠機", group: "Combination Effect", appliesTo: ["equip"] },
  120: { en: "Light Relic", jp: "輝具", group: "Combination Effect", appliesTo: ["equip"] },
  121: { en: "Dark Relic", jp: "惨具", group: "Combination Effect", appliesTo: ["equip"] },
  122: { en: "Dragon Goblet", jp: "竜杯", group: "Combination Effect", appliesTo: ["equip"] },
  123: { en: "Tasmon", jp: "タスモン", group: "Combination Effect", appliesTo: ["equip"] },
  124: { en: "Supporter", jp: "バックアッパー", group: "Combination Effect", appliesTo: ["equip"] },
  125: { en: "True Equip", jp: "真装備", group: "True Equip", appliesTo: ["equip"] },
  // 126–140 (Equip Slots), 174 (Duplicate collab tag), 178 (All Star Alch Summon), 179 (Forever Summons) and 189 (Hero Gem Exchange) are intentionally omitted, see ORPHANED_TAG_IDS.
  141: { en: "Skill Type", jp: "スキル枠", group: "Arts Gauge", appliesTo: ["unit"] },
  142: { en: "Type A", jp: "A枠", group: "Arts Gauge", appliesTo: ["unit"] },
  143: { en: "Type B", jp: "B枠", group: "Arts Gauge", appliesTo: ["unit"] },
  144: { en: "HP Condition Type", jp: "HP条件枠", group: "Arts Gauge", appliesTo: ["unit"] },
  145: { en: "Fire Type", jp: "炎属性枠", group: "Arts Gauge", appliesTo: ["unit"] },
  146: { en: "Water Type", jp: "水属性枠", group: "Arts Gauge", appliesTo: ["unit"] },
  147: { en: "Earth Type", jp: "樹属性枠", group: "Arts Gauge", appliesTo: ["unit"] },
  149: { en: "Dark Type", jp: "闇属性枠", group: "Arts Gauge", appliesTo: ["unit"] },
  150: { en: "Non-Status Ailment Type", jp: "非状態異常枠", group: "Arts Gauge", appliesTo: ["unit"] },
  152: { en: "Unique", jp: "固有枠", group: "Arts Gauge", appliesTo: ["unit"] },
  153: { en: "Collab Type", jp: "コラボ枠", group: "Arts Gauge", appliesTo: ["unit"] },
  154: { en: "Continuous Self-DMG", jp: "自身への持続ダメージ", group: "Self-DMG", appliesTo: ["unit","equip"] },
  155: { en: "Percentage Self-DMG", jp: "自身への割合ダメージ", group: "Self-DMG", appliesTo: ["unit","equip"] },
  156: { en: "Fixed Self-DMG", jp: "自身への固定ダメージ", group: "Self-DMG", appliesTo: ["equip"] },
  157: { en: "Physical Equip CT UP", jp: "物攻装備CTUP", group: "Speed / CT", appliesTo: ["unit"] },
  158: { en: "Magic Equip CT UP", jp: "魔攻装備CTUP", group: "Speed / CT", appliesTo: ["unit"] },
  159: { en: "Defense Equip CT UP", jp: "防御装備CTUP", group: "Speed / CT", appliesTo: ["unit"] },
  160: { en: "Heal Equip CT UP", jp: "回復装備CTUP", group: "Speed / CT", appliesTo: ["unit"] },
  161: { en: "Support Equip CT UP", jp: "援護装備CTUP", group: "Speed / CT", appliesTo: ["unit"] },
  162: { en: "Shorten CT", jp: "CT短縮", group: "Speed / CT", appliesTo: ["unit"] },
  163: { en: "Permanent Gacha", jp: "常設ガチャ", group: "Source", appliesTo: ["equip"] },
  164: { en: "Side Story / Main Story", jp: "外伝・メイン", group: "Source", appliesTo: ["equip"] },
  165: { en: "Raid / Giant Boss", jp: "襲来・巨大ボス", group: "Source", appliesTo: ["equip"] },
  166: { en: "Event", jp: "イベント", group: "Source", appliesTo: ["equip"] },
  167: { en: "Dungeon of Trials / Ruins", jp: "遺跡・回廊", group: "Source", appliesTo: ["equip"] },
  168: { en: "Raid", jp: "レイド", group: "Source", appliesTo: ["equip"] },
  169: { en: "Mines", jp: "鉱山", group: "Source", appliesTo: ["equip"] },
  170: { en: "Valentine", jp: "バレンタイン", group: "Source", appliesTo: ["equip"] },
  171: { en: "Summer", jp: "サマー", group: "Source", appliesTo: ["equip"] },
  172: { en: "Halloween", jp: "ハロウィン", group: "Source", appliesTo: ["equip"] },
  173: { en: "Christmas", jp: "クリスマス", group: "Source", appliesTo: ["equip"] },
  175: { en: "Permanent Gacha", jp: "常設ガチャ", group: "Source", appliesTo: ["unit"] },
  176: { en: "Raid / Side Story", jp: "襲来・外伝", group: "Source", appliesTo: ["unit"] },
  177: { en: "Gacha", jp: "降臨祭・英雄祭", group: "Source", appliesTo: ["unit"] },
  180: { en: "Valentine", jp: "バレンタイン", group: "Source", appliesTo: ["unit"] },
  181: { en: "Summer", jp: "サマー", group: "Source", appliesTo: ["unit"] },
  182: { en: "Halloween", jp: "ハロウィン", group: "Source", appliesTo: ["unit"] },
  183: { en: "Collab", jp: "コラボ", group: "Source", appliesTo: ["unit","equip"] },
  184: { en: "Limited Hero Exchange Ticket", jp: "限定英雄交換チケット", group: "Source", appliesTo: ["unit"] },
  185: { en: "Other", jp: "その他", group: "Source", appliesTo: ["unit"] },
  186: { en: "Crafting Recipe", jp: "レシピ生成", group: "Source", appliesTo: ["equip"] },
  187: { en: "Other", jp: "その他", group: "Source", appliesTo: ["equip"] },
  188: { en: "Kyuon", jp: "キュオン", group: "Combination Effect", appliesTo: ["equip"] },
  190: { en: "Rainbow Orb Exchange", jp: "虹玉交換", group: "Source", appliesTo: ["equip"] },
  191: { en: "Protagonize", jp: "主人公化", group: "Protagonize", appliesTo: ["unit"] },
  192: { en: "Domination Type", jp: "覇道型", group: "Protagonize", appliesTo: ["unit"] },
  193: { en: "Human Type", jp: "人族枠", group: "Arts Gauge", appliesTo: ["unit"] },
  194: { en: "Beast Type", jp: "獣族枠", group: "Arts Gauge", appliesTo: ["unit"] },
  199: { en: "God Type", jp: "神族枠", group: "Arts Gauge", appliesTo: ["unit"] },
  201: { en: "Destiny Type", jp: "天命型", group: "Protagonize", appliesTo: ["unit"] },
  202: { en: "CRI Rate Down", jp: "CRI率DOWN", group: "Reducing DMG", appliesTo: ["unit"] },
  203: { en: "Destruction Type", jp: "破壊型", group: "Protagonize", appliesTo: ["unit"] },
};

export const ORPHANED_TAG_IDS = Object.freeze([
  126, 127, 128, 129, 130, 131, 132, 133, 134, 135, 136, 137, 138, 139, 140, 174, 178, 179, 189,
]);

const ORPHANED_TAG_ID_SET = new Set(ORPHANED_TAG_IDS);

export function isOrphanedTagId(id) {
  return ORPHANED_TAG_ID_SET.has(Number(id));
}

export function getKnownTagIds(tags) {
  if (!Array.isArray(tags)) return [];
  return tags
    .map(Number)
    .filter((id) => Number.isFinite(id) && TAG_DICTIONARY[id] != null);
}

export function getTag(id) {
  if (isOrphanedTagId(id)) return undefined;
  return TAG_DICTIONARY[id];
}

export function getTagLabel(id) {
  if (isOrphanedTagId(id)) return null;
  return TAG_DICTIONARY[id]?.en ?? null;
}

export function getTagLabelJp(id) {
  if (isOrphanedTagId(id)) return null;
  return TAG_DICTIONARY[id]?.jp ?? null;
}

export function getAllTags(appliesTo = "all") {
  return Object.entries(TAG_DICTIONARY)
    .map(([id, def]) => ({ id: Number(id), ...def }))
    .filter((t) => appliesTo === "all" || t.appliesTo.includes(appliesTo))
    .sort((a, b) => a.id - b.id);
}

export function getTagGroups(appliesTo = "all") {
  const seen = new Set();
  const groups = [];
  for (const t of getAllTags(appliesTo)) {
    if (!seen.has(t.group)) {
      seen.add(t.group);
      groups.push(t.group);
    }
  }
  return groups;
}

export function entityMatchesSelectedTags(entityTags, selectedTags, mode = "or") {
  if (!selectedTags || selectedTags.length === 0) return true;
  const known = new Set(
    Array.isArray(entityTags)
      ? entityTags.map(Number).filter((n) => Number.isFinite(n))
      : []
  );
  if (mode === "and") {
    return selectedTags.every((id) => known.has(Number(id)));
  }
  return selectedTags.some((id) => known.has(Number(id)));
}
